import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { adminAPI } from '../../lib/api';
import { 
  Users, 
  ChartBar, 
  Stethoscope,
  UserCircle,
  FirstAidKit,
  ShieldCheck,
  CaretRight,
  ClipboardText
} from '@phosphor-icons/react';

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, usersRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getUsers(),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'patient': return UserCircle;
      case 'cma': return FirstAidKit;
      case 'provider': return Stethoscope;
      case 'admin': return ShieldCheck;
      default: return UserCircle;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'patient': return 'bg-blue-100 text-blue-700';
      case 'cma': return 'bg-teal-100 text-teal-700';
      case 'provider': return 'bg-purple-100 text-purple-700';
      case 'admin': return 'bg-amber-100 text-amber-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  const usersByRole = {
    patient: users.filter(u => u.role === 'patient').length,
    cma: users.filter(u => u.role === 'cma').length,
    provider: users.filter(u => u.role === 'provider').length,
    admin: users.filter(u => u.role === 'admin').length,
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Admin Dashboard
        </h1>
        <p className="text-slate-500 mt-1">Welcome, {user?.first_name}. System overview and management.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="kpi-card card-hover" data-testid="admin-total-users">
          <div className="flex items-center justify-between">
            <div>
              <div className="kpi-value">{users.length}</div>
              <div className="kpi-label">Total Users</div>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users size={24} className="text-blue-600" />
            </div>
          </div>
        </div>

        <div className="kpi-card card-hover" data-testid="admin-total-patients">
          <div className="flex items-center justify-between">
            <div>
              <div className="kpi-value">{stats?.total_patients || 0}</div>
              <div className="kpi-label">Total Patients</div>
            </div>
            <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
              <UserCircle size={24} className="text-teal-600" />
            </div>
          </div>
        </div>

        <div className="kpi-card card-hover" data-testid="admin-total-providers">
          <div className="flex items-center justify-between">
            <div>
              <div className="kpi-value">{stats?.total_providers || 0}</div>
              <div className="kpi-label">Providers</div>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Stethoscope size={24} className="text-purple-600" />
            </div>
          </div>
        </div>

        <div className="kpi-card card-hover" data-testid="admin-active-encounters">
          <div className="flex items-center justify-between">
            <div>
              <div className="kpi-value">{stats?.active_encounters || 0}</div>
              <div className="kpi-label">Active Encounters</div>
            </div>
            <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
              <ClipboardText size={24} className="text-amber-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Role Distribution */}
      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="card-clinical p-6">
          <h3 className="font-semibold text-slate-900 mb-4">Users by Role</h3>
          <div className="space-y-4">
            {Object.entries(usersByRole).map(([role, count]) => {
              const Icon = getRoleIcon(role);
              const percentage = users.length > 0 ? (count / users.length * 100).toFixed(0) : 0;
              return (
                <div key={role} className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getRoleBadgeColor(role).split(' ')[0]}`}>
                    <Icon size={16} className={getRoleBadgeColor(role).split(' ')[1]} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-slate-700 capitalize">{role}s</span>
                      <span className="text-sm text-slate-500">{count}</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full">
                      <div 
                        className={`h-1.5 rounded-full ${getRoleBadgeColor(role).includes('blue') ? 'bg-blue-500' : 
                          getRoleBadgeColor(role).includes('teal') ? 'bg-teal-500' :
                          getRoleBadgeColor(role).includes('purple') ? 'bg-purple-500' : 'bg-amber-500'}`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-2 card-clinical p-6">
          <h3 className="font-semibold text-slate-900 mb-4">System Status</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-sm font-medium text-green-700">API Services</span>
              </div>
              <p className="text-xs text-green-600">All systems operational</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-sm font-medium text-green-700">AI Services</span>
              </div>
              <p className="text-xs text-green-600">GPT-5.2 connected</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-sm font-medium text-green-700">Database</span>
              </div>
              <p className="text-xs text-green-600">MongoDB connected</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 mb-1">
                <ChartBar size={16} className="text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Pending Intakes</span>
              </div>
              <p className="text-lg font-bold text-blue-700">{stats?.pending_intakes || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card-clinical">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">Recent Users</h2>
            <a href="/admin/users" className="text-teal-600 text-sm font-medium flex items-center gap-1">
              Manage Users <CaretRight size={14} />
            </a>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="table-clinical" data-testid="users-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {users.slice(0, 10).map((u) => {
                const Icon = getRoleIcon(u.role);
                return (
                  <tr key={u.id} className="queue-row">
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                          <Icon size={16} className="text-slate-500" />
                        </div>
                        <span className="font-medium text-slate-900">{u.first_name} {u.last_name}</span>
                      </div>
                    </td>
                    <td className="text-slate-600">{u.email}</td>
                    <td>
                      <span className={`status-badge ${getRoleBadgeColor(u.role)}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="text-slate-500">
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
